/**
 * 动作替换候选（§16）。
 *
 * 筛选顺序（对动作库逐条过滤 + 排序）：
 *   1. 相同主要肌群；
 *   2. 相同或相近动作模式；
 *   3. 当前伤情允许（evaluateExerciseEligibility 派生可用性，硬锁动作保留展示但标记 hard_lock）；
 *   4. 前足负荷不高于原动作；
 *   5. 平衡需求不高于原动作；
 *   6. 稳定性不低于原动作（用户主动选择除外 → 标记 relative_risk_up + 风险说明）；
 *   7. 可在当前器械条件下执行（= 3，结构化标签判定）；
 *   8. 不与当日其他动作重复（alreadyUsed）；
 *   9. 用户过去表现良好（likedExerciseIds）优先；
 *   10. 排除用户标记不喜欢/不可用（dislikedExerciseIds）。
 *
 * 决策 14.13：绝对硬锁（医疗锁/与约束冲突/前足超限）→ blocker="hard_lock"，不可选择；
 * 相对风险升高（稳定性更低/平衡更高/前足更高）→ blocker="relative_risk_up"，允许但需二次确认。
 *
 * 替换后：原动作保留历史，新动作使用自己的历史（本函数不合并历史/重量趋势）。
 */

import type { Exercise, WeightUnitScope } from "@jilian/shared";
import { RULES_VERSION } from "@jilian/shared";
import type { AlternativeSuggestion, FindAlternativesInput } from "./interface";
import { evaluateExerciseEligibility } from "./eligibility";

const FOREFOOT_RANK: Record<Exercise["forefootLoad"], number> = {
  none_or_minimal: 0,
  low: 1,
  medium: 2,
  high: 3,
};

const BALANCE_RANK: Record<Exercise["balanceDemand"], number> = {
  low: 0,
  medium: 1,
  high: 2,
};

const STABILITY_RANK: Record<Exercise["stabilityLevel"], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const scopeForExercise = (e: Exercise): WeightUnitScope => {
  switch (e.incrementType) {
    case "per_dumbbell":
      return "per_dumbbell";
    case "barbell_total":
      return "total";
    case "bodyweight":
      return "bodyweight";
    case "machine_stack":
    default:
      return "machine_display";
  }
};

export function findAlternatives(input: FindAlternativesInput): AlternativeSuggestion[] {
  const { from, injury, alreadyUsed, library } = input;
  const liked = input.likedExerciseIds ?? new Set<string>();
  const disliked = input.dislikedExerciseIds ?? new Set<string>();
  const history = input.history ?? new Map<string, { weightKg: number; weightUnitScope: WeightUnitScope }>();

  const candidates: AlternativeSuggestion[] = [];

  for (const e of library) {
    if (e.id === from.id) continue;
    if (disliked.has(e.id)) continue;
    // §16.8：不与当日其他动作重复（替换动作本身不在「已用」集合内——它替换的是当前动作）
    if (alreadyUsed.has(e.id)) continue;

    const samePrimary = e.primaryMuscles.some((m) => from.primaryMuscles.includes(m));
    // 仅保留“同肌群同模式”或“alternativeIds 明确指向”的候选（§16.1/.2），保证推荐相关
    const explicitlyLinked = (from.alternativeIds ?? []).includes(e.id);
    if (!samePrimary && !explicitlyLinked) continue;
    if (samePrimary && e.movementPattern !== from.movementPattern && !explicitlyLinked) continue;

    const avail = evaluateExerciseEligibility({ exercise: e, injury });
    const rec = history.get(e.id);

    if (!avail.isEnabledForCurrentInjury) {
      // 绝对硬锁：展示但不可选（决策 14.13）
      candidates.push({
        exercise: e,
        reasonCodes: [...avail.reasonCodes],
        hasHistory: rec != null,
        previousWeightKg: rec?.weightKg ?? null,
        weightUnitScope: rec?.weightUnitScope ?? scopeForExercise(e),
        blocker: "hard_lock",
        riskNote: avail.reasonCodes.includes("LOCKED_UNTIL_MEDICAL_CLEARANCE")
          ? "医疗许可前锁定，不能用普通替换解锁。"
          : `当前伤情下不可用（${avail.reasonCodes.join(" / ")}）。`,
      });
      continue;
    }

    // 相对风险升高判定（§16.4/.5/.6 不高于原动作；稳定性不低于原动作）
    const riskNotes: string[] = [];
    const forefootUp = FOREFOOT_RANK[e.forefootLoad] > FOREFOOT_RANK[from.forefootLoad];
    const balanceUp = BALANCE_RANK[e.balanceDemand] > BALANCE_RANK[from.balanceDemand];
    const stabilityDown = STABILITY_RANK[e.stabilityLevel] > STABILITY_RANK[from.stabilityLevel];
    if (forefootUp) riskNotes.push(`前足负荷 ${e.forefootLoad} 高于「${from.forefootLoad}」`);
    if (balanceUp) riskNotes.push(`平衡需求 ${e.balanceDemand} 高于「${from.balanceDemand}」`);
    if (stabilityDown) riskNotes.push(`稳定性 ${e.stabilityLevel} 低于「${from.stabilityLevel}」`);
    const riskUp = riskNotes.length > 0;

    candidates.push({
      exercise: e,
      reasonCodes: [
        samePrimary ? "SAME_PRIMARY_MUSCLE" : "LINKED_ALTERNATIVE",
        e.movementPattern === from.movementPattern ? "SAME_MOVEMENT_PATTERN" : "CLOSE_MOVEMENT_PATTERN",
        liked.has(e.id) ? "USER_LIKED" : "NOT_RATED",
      ],
      hasHistory: rec != null,
      previousWeightKg: rec?.weightKg ?? null,
      weightUnitScope: rec?.weightUnitScope ?? scopeForExercise(e),
      blocker: riskUp ? "relative_risk_up" : "none",
      riskNote: riskUp ? riskNotes.join("；") + "，需确认后使用。" : null,
    });
  }

  // §16.9 排序：直接关联/同肌群/同模式优先 → 用户偏好 → 前足负荷低 → 平衡低 → 稳定性高
  const samePrimary = (s: AlternativeSuggestion) => s.exercise.primaryMuscles.some((m) => from.primaryMuscles.includes(m));
  const isLinked = (s: AlternativeSuggestion) => (from.alternativeIds ?? []).includes(s.exercise.id);
  candidates.sort((a, b) => {
    const score = (s: AlternativeSuggestion): number => {
      let v = 0;
      if (isLinked(s)) v -= 15; // 动作定义中明确指定的替代动作优先
      if (!samePrimary(s)) v += 100;
      if (s.exercise.movementPattern !== from.movementPattern) v += 20;
      if (!liked.has(s.exercise.id)) v += 5;
      v += FOREFOOT_RANK[s.exercise.forefootLoad] * 3;
      v += BALANCE_RANK[s.exercise.balanceDemand] * 2;
      v += STABILITY_RANK[s.exercise.stabilityLevel];
      if (s.blocker === "hard_lock") v += 500; // 硬锁排最后（仍可见，解释原因）
      if (s.blocker === "relative_risk_up") v += 10;
      return v;
    };
    return score(a) - score(b);
  });

  // §30.4：最多展示 5 个优先替代
  return candidates.slice(0, 5);
}

export { RULES_VERSION };